import { setUser } from '@sentry/react';
import type { StateCreator } from 'zustand';
import { get as apiGet } from '../data/apiClient';
import type { BoundStoreType } from '../data/store';
import type { DbUser } from '../User/db';

/** The authenticated user identity (Laravel session). */
export type AuthUser = {
  id: string;
  email: string | null;
};

export interface UserSlice {
  registrationEnabled: boolean;
  subscribeUser: () => () => void;

  authUser: undefined | AuthUser;
  authUserLoading: boolean;
  authUserError: string | null;
  sessionEpoch: number;

  currentUser: DbUser | undefined;
  setCurrentUser: (user: DbUser | undefined) => void;
  refreshCurrentUser: () => Promise<void>;
  /** Forget the cached user + all cached trip data (call on logout). */
  clearSession: () => void;
}

export const createUserSlice: StateCreator<
  BoundStoreType,
  [],
  [],
  UserSlice
> = (set, get) => {
  return {
    registrationEnabled: false,
    authUser: undefined,
    authUserLoading: true,
    authUserError: null,
    sessionEpoch: 0,

    currentUser: undefined,
    subscribeUser: () => {
      let disposed = false;
      void apiGet<{ user: DbUser | null; registrationEnabled: boolean }>(
        '/api/auth/me',
      )
        .then(({ user, registrationEnabled }) => {
          if (disposed) return;
          const cachedUser = get().currentUser;
          if (
            !user ||
            (cachedUser &&
              (cachedUser.id !== user.id || cachedUser.role !== user.role))
          ) {
            get().clearSession();
          }
          set(() => ({
            authUser: user
              ? { id: user.id, email: user.email ?? null }
              : undefined,
            currentUser: user ?? undefined,
            registrationEnabled: registrationEnabled !== false,
            authUserLoading: false,
            authUserError: null,
          }));
          if (process.env.SENTRY_ENABLED && user) {
            setUser({
              id: user.id,
              email: user.email ?? undefined,
            });
          }
        })
        .catch((error: unknown) => {
          if (disposed) return;
          get().clearSession();
          set(() => ({
            authUser: undefined,
            currentUser: undefined,
            authUserLoading: false,
            authUserError:
              error instanceof Error ? error.message : 'Unable to load session',
          }));
        });
      return () => {
        disposed = true;
      };
    },
    setCurrentUser: (user) => {
      set(() => ({
        currentUser: user,
      }));
    },
    refreshCurrentUser: async () => {
      try {
        const response = await apiGet<{
          user: DbUser | null;
          registrationEnabled: boolean;
        }>('/api/auth/me');
        set(() => ({
          registrationEnabled: response.registrationEnabled !== false,
        }));
        const user = response.user;
        if (!user) get().clearSession();
        else {
          const cachedUser = get().currentUser;
          if (
            cachedUser &&
            (cachedUser.id !== user.id || cachedUser.role !== user.role)
          )
            get().clearSession();
          set(() => ({
            currentUser: user,
            authUser: { id: user.id, email: user.email ?? null },
            authUserLoading: false,
            authUserError: null,
          }));
        }
      } catch (error) {
        get().clearSession();
        throw error;
      }
    },
    clearSession: () => {
      // Wipe the cached user and every cached domain collection so a different
      // user logging in on the same browser never sees the previous session's
      // trips. Also remove the persisted localStorage snapshot.
      set((state) => ({
        sessionEpoch: state.sessionEpoch + 1,
        currentUser: undefined,
        authUser: undefined,
        dialogs: [],
        isConfirmingPopDialogActive: false,
        confirmingDialogProps: undefined,
        currentTripId: undefined,
        tripMeta: {},
        trip: {},
        tripLocalState: {},
        comment: {},
        commentGroup: {},
        commentUser: {},
        macroplan: {},
        expense: {},
        accommodation: {},
        activity: {},
        trips: {},
        archivedTrips: {},
        tripUser: {},
        task: {},
        taskList: {},
      }));
      try {
        localStorage.removeItem('ikuyo-storage');
        sessionStorage.removeItem('ikuyo-storage');
      } catch {
        // Storage may be unavailable (private mode); the in-memory reset above
        // still protects against cross-user trip leakage.
      }
    },
  };
};
